import {
    type ViewStyle,
    type TextStyle,
    StyleSheet
} from "react-native";
import type {
    TableMeasuresConstantType,
    TableDynamicStyleType,
    TableMeasures,
    TableSize
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getMotionTransition
} from "../../helpers/motion";
import {
    webStyle
} from "../../utils";

export const TABLE_BODY_VARIANTS: Record<TableSize, keyof NCoreUIKit.Typography> = {
    medium: "bodyMediumSize",
    large: "bodyMediumSize",
    small: "bodySmallSize"
};

export const TABLE_HEAD_VARIANTS: Record<TableSize, keyof NCoreUIKit.Typography> = {
    medium: "labelMediumSize",
    large: "labelMediumSize",
    small: "labelSmallSize"
};

export const TABLE_CELL_MIN_WIDTH_FACTOR = 3;

export const getTableStickyZIndexes = ({
    layers
}: {
    layers: NCoreUIKit.ShapeConfigs["layers"];
}) => ({
    corner: layers.sticky + 2,
    header: layers.sticky + 1,
    column: layers.sticky
});

export const getTableMeasures = ({
    controlPaddings,
    controlHeights,
    touchTargets,
    pointerType,
    spaces,
    size
}: TableMeasuresConstantType): TableMeasures => {
    const controlHeight = controlHeights[size];

    return {
        rowHeight: pointerType === "coarse" ? Math.max(controlHeight, touchTargets.preferred) : controlHeight,
        cellMinWidth: controlHeights.medium * TABLE_CELL_MIN_WIDTH_FACTOR,
        bodyVariant: TABLE_BODY_VARIANTS[size],
        headVariant: TABLE_HEAD_VARIANTS[size],
        cellPadding: controlPaddings.small,
        inset: spaces.spacingMd
    };
};

const stylesheet = StyleSheet.create({
    scroller: {
        flexShrink: 1,
        minHeight: 0,
        ...webStyle({
            overscrollBehaviorX: "contain",
            overflowX: "auto",
            overflowY: "auto"
        })
    },
    content: {
        flexGrow: 1,
        ...webStyle({
            alignSelf: "flex-start",
            minWidth: "100%"
        })
    },
    fade: {
        pointerEvents: "none",
        position: "absolute",
        end: 0,
        top: 0
    },
    container: {
        alignSelf: "stretch",
        flexShrink: 1,
        minWidth: 0
    },
    nativeContent: {
        minWidth: "100%",
        flexGrow: 1
    },
    scrollerFrame: {
        flexShrink: 1,
        minHeight: 0
    }
});

export const useStyles = ({
    isReduceMotionEnabled,
    contentHeight,
    isFadeVisible,
    isOverflowing,
    background,
    direction,
    durations,
    spaces
}: TableDynamicStyleType) => {
    const styles = {
        fade: {
            width: spaces.spacingMd + spaces.spacingSm,
            opacity: isFadeVisible ? 1 : 0,
            height: contentHeight,
            ...webStyle({
                backgroundImage: `linear-gradient(to ${direction === "rtl" ? "left" : "right"}, transparent, ${background})`,
                ...getMotionTransition({
                    isReduceMotionEnabled,
                    intent: "hover",
                    properties: [
                        "opacity"
                    ],
                    durations
                })
            })
        } as Mutable<ViewStyle>,
        scroller: {
            paddingBottom: isOverflowing ? spaces.spacingSm : 0
        } as Mutable<ViewStyle>,
        caption: {
            paddingHorizontal: spaces.spacingMd,
            paddingVertical: spaces.spacingSm
        } as Mutable<TextStyle>,
        content: {
            backgroundColor: background
        } as Mutable<ViewStyle>
    };

    return styles;
};
export default stylesheet;
